<script setup lang="ts">
  import { Copy, Megaphone, Pen, Trash } from "lucide-vue-next";
  import { IndexTable, IndexTableName, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import type { JcdBenchmarkWithStats } from "@/types";

  defineProps<{
    benchmarks: JcdBenchmarkWithStats[];
    loading: boolean;
  }>();

  const emit = defineEmits<{
    edit: [benchmark: JcdBenchmarkWithStats];
    duplicate: [benchmark: JcdBenchmarkWithStats];
    delete: [benchmark: JcdBenchmarkWithStats];
  }>();

  const columns: DataTableColumn<JcdBenchmarkWithStats>[] = [
    { key: "name", label: "Benchmark" },
    { key: "reco", label: "Reco", width: 110 },
    { key: "attribution", label: "Attribution", width: 110 },
    { key: "intention", label: "Intention", width: 110 },
  ];

  const SCORE_KEYS = ["reco", "attribution", "intention"] as const;
  const formatPct = (value: number | null) => (value == null ? null : `${Math.round(value * 100)}%`);

  const actionsFor = (benchmark: JcdBenchmarkWithStats) => [
    { isDelete: false, function: () => emit("duplicate", benchmark), text: "Dupliquer", icon: Copy },
    { isDelete: true, function: () => emit("delete", benchmark), msgConfirm: "ce benchmark", text: "Supprimer", icon: Trash },
  ];
</script>

<template>
  <IndexTable
    :items="benchmarks"
    :columns="columns"
    :loading="loading"
    actions-class="w-[150px]"
    :skeleton-rows="5"
    empty-message="Aucun benchmark."
    @row-click="(benchmark) => emit('edit', benchmark)">
    <template #cell-name="{ item }">
      <IndexTableName :name="item.name">
        <Badge
          variant="outline"
          class="gap-1 text-2xs font-normal text-muted-foreground">
          <Megaphone class="size-3" />
          {{ item.campaigns_count }}
        </Badge>
      </IndexTableName>
    </template>
    <template
      v-for="key in SCORE_KEYS"
      :key="key"
      #[`cell-${key}`]="{ item }">
      <span
        v-if="formatPct(item[key])"
        class="font-semibold tabular-nums text-primary">
        {{ formatPct(item[key]) }}
      </span>
      <IndexTableEmpty v-else />
    </template>
    <template #actions="{ item }">
      <div
        class="flex items-center justify-end gap-1"
        @click.stop>
        <Button
          variant="ghost"
          size="sm"
          class="text-primary"
          @click="emit('edit', item)">
          <Pen />
          Modifier
        </Button>
        <ActionsDropdown
          :actions="actionsFor(item)"
          :token="String(item.id)"
          delete-title="Supprimer le benchmark ?"
          delete-description="Êtes-vous sûr de vouloir supprimer ce benchmark ? Cette action est irréversible."
          delete-cancel-label="Annuler"
          delete-confirm-label="Supprimer" />
      </div>
    </template>
  </IndexTable>
</template>
